/* eslint-disable react-hooks/set-state-in-effect */
import { useCallback, useEffect, useState } from 'react'
import { kanbify } from '../lib/kanbifyClient'
import { logDevError } from '../lib/logger'

/* ========== CHECKLISTS ========== */
export function useChecklists(cardId) {
    const [checklists, setChecklists] = useState([])

    const sortChecklistItems = useCallback((items = []) => (
        [...items].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    ), [])

    const fetch = useCallback(async () => {
        if (!cardId) return
        const { data } = await kanbify
            .from('checklists')
            .select('*, checklist_items ( *, completions:checklist_item_completions ( id, user_id, completed_at, quantity ) )')
            .eq('card_id', cardId)
            .order('position')
        setChecklists((data || []).map((checklist) => ({
            ...checklist,
            checklist_items: sortChecklistItems(checklist.checklist_items || []),
        })))
    }, [cardId, sortChecklistItems])

    useEffect(() => { fetch() }, [fetch])

    useEffect(() => {
        if (!cardId) return

        let timeout
        const queueFetch = () => {
            clearTimeout(timeout)
            timeout = setTimeout(() => fetch(), 250)
        }

        const channel = kanbify
            .channel(`checklists:${cardId}`)
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'checklists',
                filter: `card_id=eq.${cardId}`,
            }, queueFetch)
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'checklist_items',
            }, queueFetch)
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'checklist_item_completions',
            }, queueFetch)
            .subscribe()

        return () => {
            kanbify.removeChannel(channel)
            clearTimeout(timeout)
        }
    }, [cardId, fetch])

    const addChecklist = async (name = 'Checklist') => {
        const { data: newChecklist, error } = await kanbify
            .from('checklists')
            .insert([{
                card_id: cardId,
                name: name,
                position: checklists.length
            }])
            .select()
            .single()

        if (error || !newChecklist) return null

        const { data } = await kanbify
            .from('checklists')
            .select('*, checklist_items ( *, completions:checklist_item_completions ( id, user_id, completed_at, quantity ) )')
            .eq('id', newChecklist.id)
            .single()
        if (data) setChecklists(p => [...p, data])
        return data
    }
    const removeChecklist = async (id) => {
        const { error } = await kanbify.from('checklists').delete().eq('id', id)
        if (error) {
            logDevError('Failed to remove checklist:', error.message)
            return { success: false, error: error.message }
        }
        setChecklists(p => p.filter(c => c.id !== id))
        return { success: true }
    }
    const addItem = async (checklistId, text) => {
        const cl = checklists.find(c => c.id === checklistId)
        const pos = cl?.checklist_items?.length || 0
        const { data: newItem, error } = await kanbify
            .from('checklist_items')
            .insert([{
                checklist_id: checklistId,
                text: text,
                position: pos,
                is_completed: false
            }])
            .select()
            .single()

        if (error || !newItem) return null

        const { data } = await kanbify.from('checklist_items').select('*, completions:checklist_item_completions ( id, user_id, completed_at, quantity )').eq('id', newItem.id).single()
        if (data) {
            setChecklists(p => p.map(c => c.id === checklistId ? {
                ...c,
                checklist_items: sortChecklistItems([...(c.checklist_items || []), data]),
            } : c))
        }
        return data
    }
    const toggleItem = async (checklistId, itemId, userId) => {
        const cl = checklists.find(c => c.id === checklistId)
        const item = cl?.checklist_items?.find(i => i.id === itemId)
        if (!item) return

        // Save current state for rollback on error
        const prevChecklists = checklists.map(c => ({
            ...c,
            checklist_items: c.checklist_items.map(i => ({ ...i, completions: [...(i.completions || [])] }))
        }))

        // Shared item: toggle per-user completion
        if (item.is_shared) {
            const userCompleted = (item.completions || []).some(c => c.user_id === userId)
            if (userCompleted) {
                // Optimistic: remove user's completion from UI immediately
                setChecklists(p => p.map(c => c.id === checklistId ? {
                    ...c,
                    checklist_items: c.checklist_items.map(i => i.id === itemId
                        ? { ...i, completions: (i.completions || []).filter(comp => comp.user_id !== userId) }
                        : i)
                } : c))

                const { error } = await kanbify
                    .from('checklist_item_completions')
                    .delete()
                    .eq('item_id', itemId)
                    .eq('user_id', userId)

                if (error) {
                    logDevError('[toggleItem shared] rollback – delete failed:', error.message)
                    setChecklists(prevChecklists)
                    return item.is_completed
                }
                return false
            } else {
                // Optimistic: add a placeholder completion to UI immediately
                const optimisticComp = { user_id: userId, completed_at: new Date().toISOString() }
                setChecklists(p => p.map(c => c.id === checklistId ? {
                    ...c,
                    checklist_items: c.checklist_items.map(i => i.id === itemId
                        ? { ...i, completions: [...(i.completions || []), optimisticComp] }
                        : i)
                } : c))

                const { data: newComp, error } = await kanbify
                    .from('checklist_item_completions')
                    .insert([{ item_id: itemId, user_id: userId }])
                    .select()
                    .single()

                if (error || !newComp) {
                    logDevError('[toggleItem shared] rollback – insert failed:', error?.message)
                    setChecklists(prevChecklists)
                    return item.is_completed
                }

                // Replace optimistic placeholder with real DB record (includes id)
                setChecklists(p => p.map(c => c.id === checklistId ? {
                    ...c,
                    checklist_items: c.checklist_items.map(i => i.id === itemId
                        ? { ...i, completions: (i.completions || []).map(comp => comp === optimisticComp ? newComp : comp) }
                        : i)
                } : c))
                return true
            }
        }

        // Non-shared item: global toggle (original behavior)
        const newVal = !item.is_completed
        const updates = { is_completed: newVal, completed_by: newVal ? (userId ?? null) : null, completed_at: newVal ? new Date().toISOString() : null }

        // Optimistic: update UI immediately
        setChecklists(p => p.map(c => c.id === checklistId ? { ...c, checklist_items: c.checklist_items.map(i => i.id === itemId ? { ...i, ...updates } : i) } : c))

        const { error } = await kanbify
            .from('checklist_items')
            .update(updates)
            .eq('id', itemId)

        if (error) {
            logDevError('[toggleItem] rollback – update failed:', error.message)
            setChecklists(prevChecklists)
            return item.is_completed
        }

        return newVal
    }
    const toggleShared = async (checklistId, itemId, memberIds = []) => {
        const cl = checklists.find(c => c.id === checklistId)
        const item = cl?.checklist_items?.find(i => i.id === itemId)
        if (!item) return
        const newVal = !item.is_shared
        // KPI v4 UX (Teo 2026-05-26): preserve shared_member_ids when toggling
        // OFF so reactivating remembers the previous list. Only OVERWRITE
        // memberIds when turning ON AND caller passed an explicit picker
        // result; an empty/missing memberIds keeps whatever was stored.
        const updatePayload = newVal
            ? (memberIds && memberIds.length > 0
                ? { is_shared: true, shared_member_ids: memberIds }
                : { is_shared: true })
            : { is_shared: false }
        const { error } = await kanbify
            .from('checklist_items')
            .update(updatePayload)
            .eq('id', itemId)
        if (error) return
        // When switching to shared, clear global is_completed; when switching to non-shared, clear per-user completions
        if (newVal) {
            await kanbify.from('checklist_items').update({ is_completed: false, completed_by: null, completed_at: null }).eq('id', itemId)
        } else {
            await kanbify.from('checklist_item_completions').delete().eq('item_id', itemId)
        }
        setChecklists(p => p.map(c => c.id === checklistId ? {
            ...c,
            checklist_items: c.checklist_items.map(i => i.id === itemId
                ? { ...i, ...updatePayload, is_completed: false, completed_by: null, completed_at: null, completions: [] }
                : i)
        } : c))
    }
    const updateSharedMembers = async (checklistId, itemId, memberIds) => {
        const { error } = await kanbify
            .from('checklist_items')
            .update({ shared_member_ids: memberIds })
            .eq('id', itemId)
        if (error) return
        setChecklists(p => p.map(c => c.id === checklistId ? {
            ...c,
            checklist_items: c.checklist_items.map(i => i.id === itemId
                ? { ...i, shared_member_ids: memberIds }
                : i)
        } : c))
    }
    const updateItem = async (checklistId, itemId, newText) => {
        const { error } = await kanbify.from('checklist_items').update({ text: newText }).eq('id', itemId)
        if (error) {
            logDevError('Failed to update checklist item:', error.message)
            return { success: false, error: error.message }
        }
        setChecklists(p => p.map(c => c.id === checklistId ? { ...c, checklist_items: c.checklist_items.map(i => i.id === itemId ? { ...i, text: newText } : i) } : c))
        return { success: true }
    }
    const deleteItem = async (checklistId, itemId) => {
        const { error } = await kanbify.from('checklist_items').delete().eq('id', itemId)
        if (error) {
            logDevError('Failed to delete checklist item:', error.message)
            return { success: false, error: error.message }
        }
        setChecklists(p => p.map(c => c.id === checklistId ? { ...c, checklist_items: c.checklist_items.filter(i => i.id !== itemId) } : c))
        return { success: true }
    }
    const setChecklistCompletion = async (checklistId, isCompleted, userId = null) => {
        const cl = checklists.find(c => c.id === checklistId)
        const items = cl?.checklist_items || []
        const hasSharedItems = items.some(i => i.is_shared)

        // Shared items: per-user completion via checklist_item_completions
        if (hasSharedItems && userId) {
            const sharedItems = items.filter(i => i.is_shared)

            if (isCompleted) {
                // Insert per-user completion for each shared item the user hasn't completed yet
                const toInsert = sharedItems
                    .filter(item => !(item.completions || []).some(c => c.user_id === userId))
                    .map(item => ({ item_id: item.id, user_id: userId }))

                if (toInsert.length > 0) {
                    const { error } = await kanbify
                        .from('checklist_item_completions')
                        .insert(toInsert)
                        .select()
                    if (error) return { success: false, error: error.message || 'Nu s-a putut marca procedura.' }
                }

                // Optimistic update: add user completion to each shared item
                setChecklists(prev => prev.map(c => c.id === checklistId ? {
                    ...c,
                    checklist_items: c.checklist_items.map(item => {
                        if (!item.is_shared) return item
                        const alreadyDone = (item.completions || []).some(comp => comp.user_id === userId)
                        if (alreadyDone) return item
                        return { ...item, completions: [...(item.completions || []), { user_id: userId, completed_at: new Date().toISOString() }] }
                    }),
                } : c))
            } else {
                // Remove per-user completions for this user
                const itemIds = sharedItems.map(i => i.id)
                const { error } = await kanbify
                    .from('checklist_item_completions')
                    .delete()
                    .in('item_id', itemIds)
                    .eq('user_id', userId)
                if (error) return { success: false, error: error.message || 'Nu s-a putut reseta procedura.' }

                setChecklists(prev => prev.map(c => c.id === checklistId ? {
                    ...c,
                    checklist_items: c.checklist_items.map(item => {
                        if (!item.is_shared) return item
                        return { ...item, completions: (item.completions || []).filter(comp => comp.user_id !== userId) }
                    }),
                } : c))
            }

            // Also handle any non-shared items in the same checklist
            const nonSharedItems = items.filter(i => !i.is_shared)
            if (nonSharedItems.length > 0) {
                const payload = {
                    is_completed: isCompleted,
                    completed_at: isCompleted ? new Date().toISOString() : null,
                    completed_by: isCompleted ? userId : null,
                }
                await kanbify
                    .from('checklist_items')
                    .update(payload)
                    .eq('checklist_id', checklistId)
                    .eq('is_shared', false)

                setChecklists(prev => prev.map(c => c.id === checklistId ? {
                    ...c,
                    checklist_items: c.checklist_items.map(item =>
                        item.is_shared ? item : { ...item, ...payload }
                    ),
                } : c))
            }

            return { success: true, error: null }
        }

        // Non-shared items: global toggle (original behavior)
        const completedAt = isCompleted ? new Date().toISOString() : null
        const completedBy = isCompleted ? (userId ?? null) : null
        const payload = {
            is_completed: isCompleted,
            completed_at: completedAt,
            completed_by: completedBy,
        }

        const { error } = await kanbify
            .from('checklist_items')
            .update(payload)
            .eq('checklist_id', checklistId)

        if (error) {
            return { success: false, error: error.message || 'Nu s-a putut actualiza checklist-ul.' }
        }

        setChecklists((prev) => prev.map((checklist) => (
            checklist.id === checklistId
                ? {
                    ...checklist,
                    checklist_items: (checklist.checklist_items || []).map((item) => ({
                        ...item,
                        ...payload,
                    })),
                }
                : checklist
        )))

        return { success: true, error: null }
    }

    return { checklists, fetch, addChecklist, removeChecklist, addItem, toggleItem, toggleShared, updateSharedMembers, updateItem, deleteItem, setChecklistCompletion }
}
